import { isModelVariable } from "models/LLMs/LLMVariable";
import { deepCamelCase } from "../utils";

/** Property names a condition references, including sub rules. Expects the camelCased shape from `partitionExecuteConditions`. */
export function getConditionPropertyNames(condition: unknown): string[] {
    if (Array.isArray(condition))
        return condition.flatMap(getConditionPropertyNames);
    if (!condition || typeof condition !== "object") return [];

    const record = condition as Record<string, unknown>;

    return [
        ...(typeof record.propertyName === "string"
            ? [record.propertyName]
            : []),
        ...getConditionPropertyNames(record.subRules),
    ];
}

/** A condition gating on a model's output is owned by chain source / parent models. */
export function isRunCondition(condition: unknown): boolean {
    return getConditionPropertyNames(condition).some(isModelVariable);
}

/** Split because they're edited in different builder screens; shared with the save-time diff so both stay in sync. */
export function partitionExecuteConditions(executeConditions: unknown) {
    const conditions = Array.isArray(executeConditions)
        ? (deepCamelCase(executeConditions) as unknown[])
        : [];

    return {
        runConditions: conditions.filter(isRunCondition),
        metadataFilters: conditions.filter(
            (condition) => !isRunCondition(condition),
        ),
    };
}

export function stripVolatileNestedFields(
    value: unknown,
    extraKeysToRemove: string[] = [],
): unknown {
    const keysToRemove = new Set([
        "id",
        "reactKey",
        "shortPropertyName",
        "shortWriteFromVariable",
        ...extraKeysToRemove,
    ]);

    if (Array.isArray(value)) {
        return value.map((item) =>
            stripVolatileNestedFields(item, extraKeysToRemove),
        );
    }

    if (!value || typeof value !== "object") return value;

    return Object.entries(value as Record<string, unknown>).reduce<
        Record<string, unknown>
    >((result, [key, entryValue]) => {
        if (keysToRemove.has(key)) return result;
        result[key] = stripVolatileNestedFields(entryValue, extraKeysToRemove);
        return result;
    }, {});
}
